import { toastPrimitive } from "../../primitives/toast";
import { generateToastId, normalizeToastInput } from "./lib";
import { ToastContent } from "./toast-content";
import type { LuceToast, ToastOptions } from "./types";

function resolveOptions(opts: ToastOptions) {
  return {
    ...opts,
    variant: opts.variant ?? "neutral",
    icon: opts.icon ?? true,
    dismissible: opts.dismissible ?? true,
  };
}

// No warning for a toast with neither headline nor subtext: molecules log
// nothing (approval item A2, CC2)
function showResolved(opts: ReturnType<typeof resolveOptions>): string | number {
  const id = opts.id ?? generateToastId();

  return toastPrimitive.custom(
    <ToastContent
      {...opts}
      id={id}
      onDismiss={() => {
        toastPrimitive.dismiss(id);
      }}
    />,
    {
      id,
      // Always passed, even unset: the engine then uses the Toaster's duration,
      // and a same-ID update without one drops the previous toast's
      duration: opts.duration,
      dismissible: opts.dismissible,
    },
  );
}

export const toast: LuceToast = (headlineOrOptions) => {
  return showResolved(resolveOptions(normalizeToastInput(headlineOrOptions)));
};

toast.success = (headline, options) =>
  showResolved(resolveOptions({ ...options, variant: "success", headline }));

toast.info = (headline, options) =>
  showResolved(resolveOptions({ ...options, variant: "info", headline }));

toast.error = (headline, options) =>
  showResolved(resolveOptions({ ...options, variant: "error", headline }));

toast.warning = (headline, options) =>
  showResolved(resolveOptions({ ...options, variant: "warning", headline }));

toast.dismiss = (id) => {
  toastPrimitive.dismiss(id);
};

toast.promise = (promiseInput, options) => {
  const loadingId = showResolved(
    resolveOptions({
      headline: options.loading,
      variant: "info",
      icon: true,
      dismissible: false,
      duration: Number.POSITIVE_INFINITY,
    }),
  );

  // A factory runs inside the chain, so one that throws still shows the error toast
  const pending =
    typeof promiseInput === "function"
      ? Promise.resolve().then(promiseInput)
      : Promise.resolve(promiseInput);

  return pending
    .then((data) => {
      toastPrimitive.dismiss(loadingId);
      const msg = typeof options.success === "function" ? options.success(data) : options.success;
      showResolved(resolveOptions({ headline: msg, variant: "success" }));
      return data;
    })
    .catch((err: unknown) => {
      toastPrimitive.dismiss(loadingId);
      const msg = typeof options.error === "function" ? options.error(err) : options.error;
      showResolved(resolveOptions({ headline: msg, variant: "error" }));
      throw err;
    });
};
